import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ReactPaginate from "react-paginate";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useTranslation } from "react-i18next";
import ReactStars from "react-rating-stars-component";

import Utils from "../../utils";
import { getVehiclePartList } from "../../services/VehicleService";
import {
  deletePartInventoryHard,
  partInvenoryPartDelete,
} from "../../services/PartsInventory";
import { getSettingsCompanyDetails } from "../../services/settingsServices";
import { tConst, tConstStatic } from "../../utils/constantsTranslator";

const PAGE_SIZE = 12;

// Tokens used in part_status_name that should render as a coloured chip.
const STATUS_CHIP_CLASS = {
  "In Stock": "tp-part-chip tp-part-chip--green",
  "Out of Stock": "tp-part-chip tp-part-chip--grey",
  "Reserved": "tp-part-chip tp-part-chip--orange",
  "Sent to Wastage": "tp-part-chip tp-part-chip--red",
  "Sold": "tp-part-chip tp-part-chip--purple",
};

const chipClassFor = (status) => STATUS_CHIP_CLASS[status] || "tp-part-chip tp-part-chip--neutral";

const PartDetailsVehicle = () => {
  const state = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [vehiclePartDetails, setVehiclePartDetails] = useState({});
  const [delModal, setDelModal] = useState(false);
  const [delpart, setDelPart] = useState("");
  const [settingDetails, setSettingDetails] = useState("");
  const [partAttributesList, setPartAttributesList] = useState([]);
  const [delRestoreModa, setDelRestoreModal] = useState(false);
  const [delPartRestoreId, setDelPartRestoreId] = useState("");
  const [isDelButtonLoad, setIsDelButtonLoad] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  const vehicle = state?.state || {};

  const getFieldPermission = (attribute_name) => {
    if (partAttributesList?.attribute_array?.length === 0) {
      return { show: true, editable: true };
    }
    const attr = partAttributesList?.attribute_array?.find(
      (item) => item.column_name === attribute_name
    );
    if (!attr) {
      return { show: false, editable: false };
    }
    return {
      show: attr.is_view === 1,
      editable: attr.is_edit === 1,
    };
  };

  useEffect(() => {
    getSettingsCompanyDetails()
      .then((response) => setSettingDetails(response.data.data))
      .catch(() => {});
  }, []);

  const fetchParts = () => {
    if (!state?.state?.id) return;
    const data = { vehicle_id: state.state.id, part_id: 0 };
    getVehiclePartList(data)
      .then((response) => {
        setVehiclePartDetails(response.data.data);
        setPartAttributesList(response.data.data.part_inventory_attribute);
      })
      .catch((err) => toast.error(Utils.getErrorMessage(err)));
  };

  useEffect(() => {
    fetchParts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.state?.id]);

  const parts = vehiclePartDetails?.parts || [];

  // Aggregate stats for the header cards.
  const stats = useMemo(() => {
    const totalParts = parts.length;
    const toNumber = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    };
    const totalValue = parts.reduce(
      (sum, p) => sum + toNumber(p?.part_price) * toNumber(p?.quantity || 1),
      0
    );
    const activeParts = parts.filter(
      (p) => !p.deleted_at && (p.part_status_name === "In Stock" || !p.part_status_name)
    ).length;
    const soldReserved = parts.filter(
      (p) => p.part_status_name === "Sold" || p.part_status_name === "Reserved"
    ).length;
    return { totalParts, totalValue, activeParts, soldReserved };
  }, [parts]);

  const totalPages = Math.max(1, Math.ceil(parts.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages - 1);
  const pagedParts = parts.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const onDestroyPartType = (e) => {
    e.preventDefault();
    const data = { id: delpart, delete_type: 1 };
    partInvenoryPartDelete(data)
      .then((response) => {
        fetchParts();
        toast.success(response.data.message);
        setDelPart("");
        setDelModal(false);
      })
      .catch((err) => toast.error(Utils.getErrorMessage(err)));
  };

  const onDestroyassociatePartType = (e) => {
    e.preventDefault();
    const data = { id: delpart, delete_type: 2 };
    partInvenoryPartDelete(data)
      .then((response) => {
        fetchParts();
        toast.success(response.data.message);
        setDelPart("");
        setDelModal(false);
      })
      .catch((err) => toast.error(Utils.getErrorMessage(err)));
  };

  const onDeleteModalClose = () => setDelModal(false);

  const onDeleteModalRestoreOpen = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    setDelPartRestoreId(item);
    setDelRestoreModal(true);
  };

  const onDeleteRestoreModalClose = () => setDelRestoreModal(false);

  const onDestroyPartInventory = (e) => {
    e.preventDefault();
    setIsDelButtonLoad(true);
    const UpdateData = {
      record_show_type: 1,
      part_id: delPartRestoreId.id,
    };
    deletePartInventoryHard(UpdateData)
      .then((response) => {
        toast.success(response.data.message);
        setDelPartRestoreId(false);
        setIsDelButtonLoad(false);
        setDelRestoreModal(false);
        fetchParts();
      })
      .catch((err) => {
        setDelRestoreModal(false);
        toast.error(Utils.getErrorMessage(err));
      })
      .finally(() => setIsDelButtonLoad(false));
  };

  const goToDetails = (item) => {
    if (!item?.id) return;
    navigate(`/part/details/inven/${item.id}`, {
      state: { ...item, part_management_type: 1 },
    });
  };

  const formatCurrency = (value) => {
    const n = parseFloat(value);
    if (!Number.isFinite(n)) return "€ 0.00";
    return `€ ${n.toFixed(2)}`;
  };

  // Pick the best image available for a part thumbnail.
  const getThumbnail = (item) => {
    if (item?.part_image_url) return item.part_image_url;
    if (Array.isArray(item?.image_list) && item.image_list.length > 0) {
      return item.image_list[0]?.image_url;
    }
    return null;
  };

  const vehicleTitle = [vehicle?.vehicle_make_name, vehicle?.vehicle_model, vehicle?.vehicle_year]
    .filter(Boolean)
    .join(" · ");

  const plate = vehicle?.registration || vehicle?.registration_plate;

  return (
    <div className="tp-parts-details-page">
      <section className="content-header">
        <div className="container-fluid">
          {/* Vehicle hero card */}
          <div className="tp-vhero">
            <div className="tp-vhero-left">
              <button
                type="button"
                className="tp-btn-ghost"
                onClick={() => navigate("/vehicle-process")}
                aria-label={t("back_to_vehicles")}
              >
                <i className="bi bi-arrow-left"></i>
                <span>{t("back_to_vehicles")}</span>
              </button>
            </div>

            <div className="tp-vhero-main">
              <div className="tp-vhero-idblock">
                <span className="tp-vhero-idlabel">{t("internal_id")}</span>
                <span className="tp-vhero-idvalue">
                  {vehicle?.vehicle_internal_id || "—"}
                </span>
              </div>

              {plate && (
                <div className="tp-vhero-plate" title={t("registration")}>
                  <span className="tp-vhero-plate-flag">
                    <span className="tp-vhero-plate-stars">★★★★★★★★★★★★</span>
                    <span className="tp-vhero-plate-country">PT</span>
                  </span>
                  <span className="tp-vhero-plate-text">{plate}</span>
                </div>
              )}

              <div className="tp-vhero-title">
                <h1 className="tp-vhero-h1">{t("parts_details_title")}</h1>
                {vehicleTitle && (
                  <p className="tp-vhero-sub">{vehicleTitle}</p>
                )}
              </div>
            </div>
          </div>

          {/* Stats strip */}
          <div className="tp-stats-grid">
            <div className="tp-stat tp-stat--violet">
              <div className="tp-stat-icon"><i className="bi bi-boxes"></i></div>
              <div className="tp-stat-body">
                <div className="tp-stat-label">{t("total_parts")}</div>
                <div className="tp-stat-value">{stats.totalParts}</div>
              </div>
            </div>

            <div className="tp-stat tp-stat--green">
              <div className="tp-stat-icon"><i className="bi bi-currency-euro"></i></div>
              <div className="tp-stat-body">
                <div className="tp-stat-label">{t("total_value")}</div>
                <div className="tp-stat-value">{formatCurrency(stats.totalValue)}</div>
              </div>
            </div>

            <div className="tp-stat tp-stat--blue">
              <div className="tp-stat-icon"><i className="bi bi-check2-circle"></i></div>
              <div className="tp-stat-body">
                <div className="tp-stat-label">{t("active_parts_count")}</div>
                <div className="tp-stat-value">{stats.activeParts}</div>
              </div>
            </div>

            <div className="tp-stat tp-stat--orange">
              <div className="tp-stat-icon"><i className="bi bi-bag-check"></i></div>
              <div className="tp-stat-body">
                <div className="tp-stat-label">{t("sold_reserved")}</div>
                <div className="tp-stat-value">{stats.soldReserved}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          {parts.length === 0 ? (
            <div className="tp-parts-empty">
              <div className="tp-parts-empty-icon">
                <i className="bi bi-inbox"></i>
              </div>
              <h3 className="tp-parts-empty-title">{t("no_parts_yet")}</h3>
              <p className="tp-parts-empty-msg">{t("no_parts_yet_msg")}</p>
            </div>
          ) : (
            <>
              <div className="tp-parts-grid">
                {pagedParts.map((item) => {
                  const thumbnail = getThumbnail(item);
                  const statusLabel = item?.part_status_name
                    ? tConstStatic(item.part_status_name)
                    : item?.inventory_status_name
                    ? tConstStatic(item.inventory_status_name)
                    : null;
                  const statusRaw = item?.part_status_name || item?.inventory_status_name;
                  const conditionLabel = item?.condition_name
                    ? tConst(item.condition_name, t)
                    : null;

                  return (
                    <div
                      key={item.id || item.uuid}
                      className={`tp-part-card ${item.deleted_at ? "tp-part-card--deleted" : ""}`}
                    >
                      <div className="tp-part-card-thumb">
                        {thumbnail ? (
                          <img src={thumbnail} alt={item?.part_name || ""} />
                        ) : (
                          <div className="tp-part-card-thumb-fallback">
                            <i className="bi bi-image"></i>
                          </div>
                        )}
                        {statusLabel && (
                          <span className={`${chipClassFor(statusRaw)} tp-part-card-status`}>
                            {statusLabel}
                          </span>
                        )}
                      </div>

                      <div className="tp-part-card-body">
                        <div className="tp-part-card-head">
                          <h4 className="tp-part-card-title" title={item?.part_name}>
                            {item?.part_name || "—"}
                          </h4>
                          {item?.parts_internal_id && (
                            <span className="tp-part-card-code">
                              <i className="bi bi-upc"></i> {item.parts_internal_id}
                            </span>
                          )}
                        </div>

                        {(item?.catalog_name || item?.parts_catalog_name) && (
                          <div className="tp-part-card-catalog">
                            <i className="bi bi-journal-text"></i>
                            <span>
                              {item.catalog_name || item.parts_catalog_name}
                            </span>
                          </div>
                        )}

                        <div className="tp-part-card-meta">
                          {conditionLabel && (
                            <span className="tp-part-meta-item">
                              <span className="tp-part-meta-k">{t("condition")}</span>
                              <span className="tp-part-meta-v">{conditionLabel}</span>
                            </span>
                          )}
                          <span className="tp-part-meta-item">
                            <span className="tp-part-meta-k">{t("quantity")}</span>
                            <span className="tp-part-meta-v">{item?.quantity || 0}</span>
                          </span>
                          {item?.brand_name && (
                            <span className="tp-part-meta-item">
                              <span className="tp-part-meta-k">{t("brand")}</span>
                              <span className="tp-part-meta-v">{item.brand_name}</span>
                            </span>
                          )}
                        </div>

                        {typeof item?.part_rating !== "undefined" && item?.part_rating !== null && item?.part_rating !== 0 && (
                          <div className="tp-part-card-rating">
                            <ReactStars
                              count={5}
                              value={Number(item.part_rating) || 0}
                              size={16}
                              activeColor="#ffd700"
                              edit={false}
                              isHalf={true}
                            />
                          </div>
                        )}

                        <div className="tp-part-card-footer">
                          <div className="tp-part-card-price">
                            <span className="tp-part-card-price-k">{t("price")}</span>
                            <span className="tp-part-card-price-v">
                              {formatCurrency(item?.part_price)}
                            </span>
                          </div>

                          <div className="tp-part-card-actions">
                            {item.deleted_at ? (
                              <button
                                type="button"
                                className="tp-btn tp-btn-danger"
                                onClick={(e) => onDeleteModalRestoreOpen(e, item)}
                              >
                                <i className="bi bi-arrow-repeat"></i>
                                <span>{t("restore")}</span>
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="tp-btn tp-btn-primary"
                                onClick={() => goToDetails(item)}
                              >
                                <i className="bi bi-eye"></i>
                                <span>{t("view_part_details")}</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {parts.length > PAGE_SIZE && (
                <div className="d-flex justify-content-center tp-parts-pagination">
                  <ReactPaginate
                    previousLabel={<i className="bi bi-chevron-double-left"></i>}
                    nextLabel={<i className="bi bi-chevron-double-right"></i>}
                    breakLabel={"..."}
                    pageCount={totalPages}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={5}
                    onPageChange={({ selected }) => setCurrentPage(selected)}
                    forcePage={safePage}
                    containerClassName={"pagination"}
                    activeClassName={"active"}
                    pageClassName={"page-item"}
                    pageLinkClassName={"page-link"}
                    previousClassName={"page-item"}
                    previousLinkClassName={"page-link"}
                    nextClassName={"page-item"}
                    nextLinkClassName={"page-link"}
                    breakClassName={"page-item"}
                    breakLinkClassName={"page-link"}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Keep silent reference to helper so lint doesn't complain about unused
          (these are still used by modal callbacks) */}
      <span style={{ display: "none" }}>{String(!!getFieldPermission && !!settingDetails)}</span>

      <Modal isOpen={delModal}>
        <ModalHeader>{t("delete_part")}</ModalHeader>
        <ModalBody>
          <p className="mb-0">{t("are_you_sure_delete")}</p>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-block btn-primary"
            onClick={onDeleteModalClose}
            style={{ width: "100px", marginTop: "10px" }}
          >
            {t("close")} <i className="bi bi-x-lg"></i>
          </button>
          <button
            type="button"
            className="btn btn-block bg-red"
            style={{ width: "180px" }}
            onClick={onDestroyPartType}
          >
            {t("delete_from_inventory")} <i className="bi bi-trash3"></i>
          </button>
          <button
            type="button"
            className="btn btn-block bg-red"
            style={{ width: "220px" }}
            onClick={onDestroyassociatePartType}
          >
            {t("delete_associate_part")} <i className="bi bi-trash3"></i>
          </button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={delRestoreModa}>
        <ModalHeader>{t("restore_inventory_part")}</ModalHeader>
        <ModalBody>
          <p className="mb-0">{t("confirm_restore_inventory_part")}</p>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-block btn-primary"
            disabled={isDelButtonLoad}
            onClick={onDeleteRestoreModalClose}
            style={{ width: "100px", marginTop: "10px" }}
          >
            {t("close")} <i className="bi bi-x-lg"></i>
          </button>
          <button
            type="button"
            className="btn btn-block bg-red"
            style={{ width: "120px" }}
            onClick={onDestroyPartInventory}
          >
            {t("restore")} <i className="bi bi-arrow-repeat"></i>
          </button>
          {isDelButtonLoad && (
            <button type="button" disabled className="btn-square">
              <i className="fa fa-circle-o-notch fa-spin"></i> {t("loading")}...
            </button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PartDetailsVehicle;
